import { useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import medicalReviewApi from "@/services/medicalReviewApi";
import { countsSignature } from "@/views/MedicalReview/medicalReview";

const POLL_INTERVAL_MS = 60 * 1000;

/**
 * The cheap counts endpoint, polled so a reviewer learns that data arrived
 * without reloading. `hasNewData` compares the latest totals against the ones
 * the screen opened on (or the ones the reviewer last acted on), so the banner
 * only appears when the queue genuinely moved.
 */
export const useReviewCountsWatch = (trialId: string) => {
  const query = useQuery({
    queryKey: ["medical-review-counts", trialId],
    queryFn: () => medicalReviewApi.counts(trialId),
    enabled: Boolean(trialId),
    // A trial without medical review answers 403 once; polling it every minute
    // would just repeat the failure.
    refetchInterval: (activeQuery) =>
      activeQuery.state.error ? false : POLL_INTERVAL_MS,
    refetchIntervalInBackground: false,
    retry: false,
  });

  const signature = countsSignature(query.data);
  const openedOnRef = useRef<string | null>(null);
  if (signature !== null && openedOnRef.current === null) {
    openedOnRef.current = signature;
  }

  const [acknowledged, setAcknowledged] = useState<string | null>(null);
  const baseline = acknowledged ?? openedOnRef.current;

  return {
    counts: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    hasNewData: Boolean(signature && baseline && signature !== baseline),
    acknowledgeNewData: () => setAcknowledged(signature),
  };
};
